import type { ReactNode } from 'react';

import { Fragment, useMemo, useState } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
  useParams,
} from 'react-router';

import { isSyncWebhookTypeEnabled } from '../lib/helpers';

import Blocker from '../components/Blocker/Blocker';

import DirtyHandlerContext from '../contexts/DirtyHandlerContext';
import { ModalProvider } from '../contexts/ModalContext';

import { LanguageWrapper } from '../hoc/LanguageWrapper/LanguageWrapper';
import NavigatePage from '../hoc/NavigatePage/NavigatePage';
import WrapUserContext from '../hoc/WrapUserContext/WrapUserContext';
import Layout from '../layout/Layout';
import LayoutOrganization from '../layout/LayoutOrganization';
import LayoutSlim from '../layout/LayoutSlim';
import AccountActivation from '../pages/AccountActivation/AccountActivation';
import AccountDeletion from '../pages/AccountDeletion/AccountDeletion';
import AddContentObject from '../pages/AddContentObject/AddContentObject';
import AddContentTypeDefinition from '../pages/AddContentTypeDefinition/AddContentTypeDefinition';
import AddPlan from '../pages/AddPlan/AddPlan';
import AddSpace from '../pages/AddSpace/AddSpace';
import AddUserRole from '../pages/AddUserRole/AddUserRole';
import AddWebhooks from '../pages/AddWebhooks/AddWebhooks';
import ApiKeyAccess from '../pages/ApiKeyAccess/ApiKeyAccess';
import APIKeys from '../pages/APIKeys/APIKeys';
import ChangePasswordPage from '../pages/ChangePasswordPage/ChangePasswordPage';
import ChangePasswordRequest from '../pages/ChangePasswordRequest/ChangePasswordRequest';
import ContentEmpty from '../pages/ContentEmpty/ContentEmpty';
import ContentTypeDefinitions from '../pages/ContentTypeDefinitions/ContentTypeDefinitions';
import ContentTypeObjects from '../pages/ContentTypeObjects/ContentTypeObjects';
import EditMedia from '../pages/EditMedia/EditMedia';
import Home from '../pages/Home/Home';
import HomeSlim from '../pages/Home/HomeSlim';
import Login from '../pages/Login/Login';
import Logout from '../pages/Logout/Logout';
import MediaLibrary from '../pages/MediaLibrary/MediaLibrary';
import OauthConsent from '../pages/OauthConsent/OauthConsent';
import { OauthConsentRevoked } from '../pages/OauthConsent/OauthConsentRevoked';
import OauthLogin from '../pages/OauthLogin/OauthLogin';
import Page404 from '../pages/Page404/Page404';
import Plans from '../pages/Plans/Plans';
import Plugins from '../pages/Plugins/Plugins';
import Profile from '../pages/Profile/Profile';
import Register from '../pages/Register/Register';
import RegisterAppSumo from '../pages/RegisterAppSumo/RegisterAppSumo';
import SpaceModify from '../pages/SpaceModify/SpaceModify';
import Spaces from '../pages/Spaces/Spaces';
import SpaceSettings from '../pages/SpaceSettings/SpaceSettings';
import SpacesManage from '../pages/SpacesManage/SpacesManage';
import Starters from '../pages/Starters/Starters';
import UserRoles from '../pages/UserRoles/UserRoles';
import Users from '../pages/Users/Users';
import ProtectedRoute from './ProtectedRoute/ProtectedRoute';

export const ParamKeyPage = ({
  paramName,
  children,
}: {
  paramName: string;
  children: ReactNode;
}) => {
  const params = useParams();
  const key = params[paramName];
  return <Fragment key={key}>{children}</Fragment>;
};

const PageWrapper = ({ children }: { children: ReactNode }) => {
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const dirtyHandlerContextValue = useMemo(
    () => ({ dirty, setDirty, submitting, setSubmitting }),
    [dirty, submitting],
  );

  return (
    <DirtyHandlerContext value={dirtyHandlerContextValue}>
      {children}
      <Blocker dirty={dirty} submitting={submitting} />
    </DirtyHandlerContext>
  );
};

export const webhooksDefinedColumns = () =>
  isSyncWebhookTypeEnabled()
    ? ['name', 'type', 'url', 'enabled', 'actions']
    : ['name', 'url', 'enabled', 'actions'];

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/logout" element={<Logout />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<NavigatePage />}></Route>

        <Route path="/s/:spaceSlug/" element={<NavigatePage />}>
          <Route
            element={
              <PageWrapper>
                <WrapUserContext>
                  <Layout />
                </WrapUserContext>
              </PageWrapper>
            }
          >
            <Route path="" element={<Home />} />
            <Route
              path="content-type-objects/:contentTypeName"
              element={
                <ParamKeyPage paramName="contentTypeName">
                  <ContentTypeObjects />
                </ParamKeyPage>
              }
            />
            <Route
              path="content-type-objects/add/:contentTypeName"
              element={<AddContentObject mode={'add'} />}
            />
            <Route
              path="content-type-objects/edit/:contentTypeName/:id"
              element={<AddContentObject mode={'edit'} />}
            />
            <Route
              path="content-type-objects/duplicate/:contentTypeName/:id"
              element={<AddContentObject duplicate mode={'duplicate'} />}
            />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="media/edit/:id" element={<EditMedia />} />
            <Route
              path="content-type-definitions"
              element={
                <ContentTypeDefinitions limit={1000} pagination={false} />
              }
            />
            <Route
              path="content-type-definitions/add"
              element={<AddContentTypeDefinition />}
            />
            <Route
              path="content-type-definitions/edit/:contentTypeName"
              element={<AddContentTypeDefinition />}
            />
            <Route
              path="content-type-definitions/duplicate/:contentTypeName"
              element={<AddContentTypeDefinition duplicate />}
            />
            <Route path="api-keys" element={<APIKeys />} />
            <Route path="plugins" element={<Plugins />} />
            <Route
              path="webhooks"
              element={
                <ContentTypeObjects
                  allowInternals
                  definedTypeName="_webhooks"
                  definedColumns={webhooksDefinedColumns() as any}
                />
              }
            />
            <Route path="webhooks/add" element={<AddWebhooks mode={'add'} />} />
            <Route
              path="webhooks/edit/:id"
              element={<AddWebhooks mode={'edit'} />}
            />
            <Route
              path="webhooks/duplicate/:id"
              element={<AddWebhooks mode={'edit'} duplicate />}
            />
            <Route path="user-roles" element={<UserRoles />} />
            <Route path="user-roles/add" element={<AddUserRole />} />
            <Route path="user-roles/edit/:id" element={<AddUserRole />} />
            <Route
              path="user-roles/duplicate/:id"
              element={<AddUserRole duplicate />}
            />
            <Route path="content" element={<ContentEmpty />} />
            <Route path="starters" element={<Starters />} />
            <Route path="space-settings" element={<SpaceSettings />} />
          </Route>
        </Route>

        <Route element={<WrapUserContext />}>
          <Route
            element={
              <NavigatePage>
                <PageWrapper>
                  <LayoutSlim />
                </PageWrapper>
              </NavigatePage>
            }
          >
            <Route path="start" element={<HomeSlim />} />
          </Route>

          <Route
            element={
              <PageWrapper>
                <LayoutOrganization />
              </PageWrapper>
            }
          >
            <Route path="organization" element={<SpacesManage />} />
            <Route path="space/add" element={<SpaceModify mode="add" />} />
            <Route
              path="space/upgrade/:id"
              element={<SpaceModify mode="upgrade" />}
            />
            <Route
              path="space/edit/:id"
              element={<SpaceModify mode="edit" />}
            />

            <Route path="users" element={<Users />} />
            <Route
              path="users/add"
              element={<Profile mode="add" owner={undefined} />}
            />
            <Route
              path="users/edit/:id"
              element={<Profile mode="edit" owner={undefined} />}
            />
            <Route
              path="profile"
              element={<Profile owner={true} mode={'edit'} />}
            />
          </Route>
        </Route>

        <Route
          element={
            <WrapUserContext>
              <PageWrapper>
                <Layout />
              </PageWrapper>
            </WrapUserContext>
          }
        >
          <Route path="/spaces/edit/:id" element={<AddSpace />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/plans/add/" element={<AddPlan />} />
          <Route path="/plans/edit/:id" element={<AddPlan />} />

          <Route path="/users-data-preview" element={<Users isAllUsers />} />

          <Route path="/spaces-data-preview" element={<Spaces />} />
        </Route>
      </Route>

      <Route path="/:lang?/" element={<LanguageWrapper />}>
        <Route
          path="login"
          element={
            <ProtectedRoute redirect={<Login />}>
              <Navigate to="/" replace />
            </ProtectedRoute>
          }
        />
        <Route path="oauth/login" element={<OauthLogin />} />
        <Route path="oauth/consent" element={<OauthConsent />} />
        <Route path="oauth/consent/revoked" element={<OauthConsentRevoked />} />
        <Route path="register" element={<Register />} />
        <Route path="register/activate" element={<AccountActivation />} />
        <Route path="delete-account-confirm" element={<AccountDeletion />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
        <Route
          path="change-password-request"
          element={<ChangePasswordRequest />}
        />
        <Route path="register-appsumo" element={<RegisterAppSumo />} />
        <Route
          path="access-api-key"
          element={
            <ModalProvider>
              <ApiKeyAccess />
            </ModalProvider>
          }
        />
        <Route path="*" element={<Page404 />} />
      </Route>
      <Route path="*" element={<Page404 />} />
    </>,
  ),
);

const AppRouter = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
